import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { 
  ArrowRight, Globe, Zap, Shield, BarChart3, CheckCircle, 
  TrendingUp, FileText, Award, Users, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://businessmintpr.com';

// Schema.org structured data for Digital PR Service
const digitalPRSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Digital PR Services",
  "description": "Get your brand featured in India's top digital publications with high DA backlinks and guaranteed coverage.",
  "provider": {
    "@type": "Organization",
    "name": "BusinessMint PR",
    "url": SITE_URL
  },
  "serviceType": "Digital Public Relations",
  "areaServed": {
    "@type": "Country",
    "name": "India"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Digital PR Packages",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Express Digital PR",
          "description": "48-hour turnaround for urgent coverage needs"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Standard Digital PR",
          "description": "5-7 day turnaround with guaranteed placement"
        }
      }
    ]
  }
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": SITE_URL
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Services",
      "item": `${SITE_URL}/marketplace`
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Digital PR",
      "item": `${SITE_URL}/services/digital-pr`
    }
  ]
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Digital PR and how does it work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Digital PR involves getting your brand featured in online publications, news sites, and industry blogs. We connect you with verified publications, handle the editorial process, and ensure your article gets published with proper attribution and backlinks."
      }
    },
    {
      "@type": "Question",
      "name": "How long does it take to get published?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Standard turnaround is 5-7 business days. We also offer Express (48-72 hours) and Priority (3-5 days) options for faster delivery."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need to provide the article content?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You can either provide your own article or use our professional writing service. Our writers are experienced with publication requirements and editorial guidelines."
      }
    },
    {
      "@type": "Question",
      "name": "What happens if my article is rejected?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer a 100% refund guarantee. If your article is rejected by the publication, you get a full refund - no questions asked."
      }
    }
  ]
};

const DigitalPR = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/publications`);
      const data = await res.json();
      setPublications(data.slice(0, 6));
    } catch (err) {
      console.error('Error fetching publications:', err);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Globe,
      title: 'Top-Tier Publications',
      description: 'Get featured in India\'s most authoritative digital news outlets and industry publications.'
    },
    {
      icon: TrendingUp,
      title: 'High Domain Authority',
      description: 'All publications have DA 40+ ensuring strong SEO backlinks and credibility boost.'
    },
    {
      icon: Zap,
      title: '48-Hour Express',
      description: 'Need coverage fast? Our express option delivers published articles in just 48 hours.'
    },
    {
      icon: Shield,
      title: '100% Guarantee',
      description: 'Full refund if your article doesn\'t get published. Zero risk for you.'
    }
  ];

  const benefits = [
    'Permanent dofollow backlinks from high-DA sites',
    'Improved search engine rankings',
    'Enhanced brand credibility and trust',
    'Increased referral traffic',
    'Digital footprint that lasts forever',
    'Press kit material for investors and partners'
  ];

  const faqs = [
    {
      question: 'What is Digital PR and how does it work?',
      answer: 'Digital PR involves getting your brand featured in online publications, news sites, and industry blogs. We connect you with verified publications, handle the editorial process, and ensure your article gets published with proper attribution and backlinks.'
    },
    {
      question: 'How long does it take to get published?',
      answer: 'Standard turnaround is 5-7 business days. We also offer Express (48-72 hours) and Priority (3-5 days) options for faster delivery.'
    },
    {
      question: 'Do I need to provide the article content?',
      answer: 'You can either provide your own article or use our professional writing service. Our writers are experienced with publication requirements and editorial guidelines.'
    },
    {
      question: 'What happens if my article is rejected?',
      answer: 'We offer a 100% refund guarantee. If your article is rejected by the publication, you get a full refund - no questions asked.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Digital PR Services India | Get Featured in Top Publications | BusinessMint PR</title>
        <meta name="title" content="Digital PR Services India | Get Featured in Top Publications | BusinessMint PR" />
        <meta name="description" content="Get your brand featured in India's top digital publications. High DA backlinks (40+), guaranteed coverage, 48-hour express delivery. 50+ verified publications. 100% refund guarantee." />
        <meta name="keywords" content="digital PR India, online PR services, press release distribution, media coverage, high DA backlinks, brand publicity, news publication placement, PR agency India" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="BusinessMint PR" />
        <link rel="canonical" href={`${SITE_URL}/services/digital-pr`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services/digital-pr`} />
        <meta property="og:title" content="Digital PR Services | Get Featured in Top Publications" />
        <meta property="og:description" content="Get your brand featured in India's top digital publications. High DA backlinks, guaranteed coverage, and 48-hour express options." />
        <meta property="og:site_name" content="BusinessMint PR" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${SITE_URL}/services/digital-pr`} />
        <meta name="twitter:title" content="Digital PR Services | BusinessMint PR" />
        <meta name="twitter:description" content="Get your brand featured in India's top digital publications. 50+ verified publications, 100% refund guarantee." />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(digitalPRSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0d0d0d] py-20 md:py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #D4AF37 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-manrope uppercase tracking-widest mb-6">
              <Globe className="w-4 h-4" />
              Digital PR Services
            </div>
            
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
              Get Featured in{' '}
              <span className="text-[#D4AF37]">Top Digital</span>{' '}
              Publications
            </h1>
            
            <p className="font-manrope text-lg md:text-xl text-gray-400 mb-10 max-w-2xl">
              Boost your brand's online presence with editorial placements in India's most authoritative news sites and industry publications. Permanent backlinks, enhanced credibility.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/marketplace?type=digital-pr"
                className="btn-shimmer inline-flex items-center justify-center gap-3 px-10 py-5 font-manrope text-sm uppercase tracking-widest"
              >
                Browse Publications
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-3 px-10 py-5 border-2 border-[#D4AF37]/50 text-white font-manrope text-sm uppercase tracking-widest hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
              >
                How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#D4AF37] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">50+</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Publications</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">DA 40+</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Average Authority</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">48h</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Express Delivery</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">100%</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Refund Guarantee</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Why Choose Our Digital PR?
            </h2>
            <p className="font-manrope text-[#737373] max-w-2xl mx-auto">
              We've simplified the process of getting featured in top publications.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-[#fafafa] border border-[#e5e5e5] p-6 hover:border-[#D4AF37] transition-colors">
                  <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-playfair text-lg font-bold mb-2 text-[#1a1a1a]">{feature.title}</h3>
                  <p className="font-manrope text-sm text-[#737373]">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">How Digital PR Works</h2>
            <p className="font-manrope text-[#737373]">Simple 3-step process to get featured</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                <span className="font-mono text-2xl font-bold text-[#D4AF37]">01</span>
              </div>
              <h3 className="font-playfair text-xl font-bold mb-3 text-[#1a1a1a]">Select Publication</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Browse our verified publications by category, DA score, and price. Choose the perfect fit for your brand.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                <span className="font-mono text-2xl font-bold text-[#D4AF37]">02</span>
              </div>
              <h3 className="font-playfair text-xl font-bold mb-3 text-[#1a1a1a]">Submit Article</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Upload your article or use our writing service. We ensure it meets editorial guidelines.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                <span className="font-mono text-2xl font-bold text-[#D4AF37]">03</span>
              </div>
              <h3 className="font-playfair text-xl font-bold mb-3 text-[#1a1a1a]">Go Live</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Track your order in real-time. Get your live article URL once published.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#1a1a1a]">
                Benefits of Digital PR Coverage
              </h2>
              <p className="font-manrope text-[#737373] mb-8">
                Editorial placements in top publications deliver lasting value for your brand's online presence and authority.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="font-manrope text-[#525252]">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/marketplace?type=digital-pr"
                className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest font-semibold hover:bg-[#B8960F] transition-colors"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="bg-[#0d0d0d] p-8 border border-[#D4AF37]/20">
              <h3 className="font-playfair text-xl font-bold text-white mb-6">Sample Publications</h3>
              {loading ? (
                <div className="text-gray-500">Loading...</div>
              ) : (
                <div className="space-y-4">
                  {publications.map((pub) => (
                    <Link
                      key={pub.id}
                      to={`/publication/${pub.id}`}
                      className="flex items-center justify-between p-4 bg-[#1a1a1a] border border-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-colors"
                    >
                      <div>
                        <div className="font-playfair font-bold text-white">{pub.name}</div>
                        <div className="text-xs text-gray-500">{pub.category}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-mono font-bold">
                          DA {pub.da}
                        </span>
                        <ArrowRight className="w-4 h-4 text-gray-500" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              <Link
                to="/marketplace?type=digital-pr"
                className="block w-full mt-6 py-3 text-center text-[#D4AF37] font-manrope text-sm uppercase tracking-widest hover:text-white transition-colors"
              >
                View All Publications →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-[#e5e5e5] bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#fafafa] transition-colors"
                >
                  <span className="font-manrope font-semibold text-[#1a1a1a] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#737373] flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="font-manrope text-sm text-[#737373]">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#D4AF37]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#0d0d0d]">
            Ready to Get Featured?
          </h2>
          <p className="font-manrope text-[#0d0d0d]/80 mb-8 max-w-2xl mx-auto">
            Browse our verified publications and start building your digital presence today.
          </p>
          <Link
            to="/marketplace?type=digital-pr"
            className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-[#0d0d0d] text-white font-manrope text-sm uppercase tracking-widest hover:bg-black transition-all"
          >
            Browse Publications
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalPR;
